import { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';

import { Checkbox, Empty, Input } from '../ui';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface QuyenTrongLuoi {
  code: string;
  /** Mô tả người dùng đọc — dữ liệu của server, không dịch. */
  nhan: string;
}

export interface NhomTrongLuoi {
  khoa: string;
  nhan: string;
  quyen: QuyenTrongLuoi[];
}

export interface LuoiChonQuyenProps {
  nhom: NhomTrongLuoi[];
  daChon: ReadonlySet<string>;
  onDoi: (moi: Set<string>) => void;
  /** Lỗi `422` theo mã quyền — hiện ngay dưới đúng ô. */
  loiTheoQuyen?: Record<string, string>;
  voHieu?: boolean;
  /**
   * Xếp nhóm thành nhiều cột (hộp thoại rộng). Ngăn kéo hẹp để `false`: một
   * cột, đọc từ trên xuống.
   */
  luoi?: boolean;
}

/** So khớp không dấu, không phân biệt hoa thường: gõ `ke toan` vẫn ra `Kế toán`. */
function boDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/**
 * Lưới chọn quyền gom theo nhóm (service SDK, phân hệ nền tảng), kèm ô tìm.
 *
 * Mỗi nhóm một khối: ô ở đầu khối chọn cả nhóm (gạch ngang khi mới chọn một
 * phần), dưới là từng quyền theo mô tả. Mã quyền (`tri-thuc:kho:create`) không
 * hiện — người phân quyền đọc mô tả, mã chỉ là khoá của máy.
 *
 * Khi đang tìm, ô đầu khối chỉ chọn hoặc bỏ **các quyền đang hiện** của nhóm:
 * gõ `xoá` rồi chọn cả nhóm thì đúng là chọn mọi quyền xoá, không lén chọn
 * thêm quyền bị ẩn. Tên nhóm khớp từ khoá thì hiện cả nhóm.
 *
 * Chỉ lo phần trình bày; tải, lỗi và rỗng của danh mục do màn gọi tự lo.
 */
export function LuoiChonQuyen({
  nhom,
  daChon,
  onDoi,
  loiTheoQuyen = {},
  voHieu,
  luoi = false,
}: LuoiChonQuyenProps) {
  const { t } = useT();
  const [tuKhoa, setTuKhoa] = useState('');

  const nhomHien = useMemo(() => {
    const k = boDau(tuKhoa.trim());
    if (!k) return nhom;
    return nhom
      .map((n) =>
        boDau(n.nhan).includes(k)
          ? n
          : { ...n, quyen: n.quyen.filter((q) => boDau(q.nhan).includes(k) || q.code.toLowerCase().includes(k)) },
      )
      .filter((n) => n.quyen.length > 0);
  }, [nhom, tuKhoa]);

  const doiNhom = (n: NhomTrongLuoi) => {
    const moi = new Set(daChon);
    const chonHet = n.quyen.some((q) => !daChon.has(q.code));
    for (const q of n.quyen) {
      if (chonHet) moi.add(q.code);
      else moi.delete(q.code);
    }
    onDoi(moi);
  };

  const doiQuyen = (code: string) => {
    const moi = new Set(daChon);
    if (moi.has(code)) moi.delete(code);
    else moi.add(code);
    onDoi(moi);
  };

  return (
    <div className="space-y-3">
      <div className="sticky top-0 z-10 bg-surface pb-1">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-content-muted" />}
          placeholder={t('chonQuyen.timPlaceholder')}
          aria-label={t('chonQuyen.timNhan')}
          value={tuKhoa}
          onChange={(e) => setTuKhoa(e.target.value)}
        />
      </div>

      {nhomHien.length === 0 ? (
        // Rỗng vì tìm: không mô tả, không nút hành động.
        <Empty className="rounded-lg border border-dashed border-default py-8" title={t('chonQuyen.khongKhop')} />
      ) : (
        <div className={cn(luoi && 'gap-3 md:columns-2 xl:columns-3')}>
          {nhomHien.map((n) => {
            const soChon = n.quyen.filter((q) => daChon.has(q.code)).length;
            // Đếm trên cả nhóm gốc, không chỉ phần đang hiện: con số này nói vai trò có gì.
            const goc = nhom.find((g) => g.khoa === n.khoa) ?? n;
            const soChonGoc = goc.quyen.filter((q) => daChon.has(q.code)).length;
            return (
              <fieldset
                key={n.khoa}
                className="mb-3 min-w-0 break-inside-avoid rounded-lg border border-default bg-surface"
              >
                <legend className="sr-only">{n.nhan}</legend>
                <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-default bg-subtle px-3 py-2">
                  <Checkbox
                    className="min-w-0"
                    checked={soChon === n.quyen.length}
                    indeterminate={soChon > 0 && soChon < n.quyen.length}
                    disabled={voHieu}
                    aria-label={t('chonQuyen.chonCaNhom', { nhom: n.nhan })}
                    onChange={() => doiNhom(n)}
                  >
                    <span className="text-sm font-medium text-content-primary">{n.nhan}</span>
                  </Checkbox>
                  <span className="shrink-0 text-2xs tabular-nums text-content-muted">
                    {t('chonQuyen.daChonTrongNhom', { chon: soChonGoc, tong: goc.quyen.length })}
                  </span>
                </div>
                <ul>
                  {n.quyen.map((q) => {
                    const loiO = loiTheoQuyen[q.code];
                    return (
                      <li key={q.code} className="px-3 py-1.5">
                        <Checkbox
                          checked={daChon.has(q.code)}
                          disabled={voHieu}
                          onChange={() => doiQuyen(q.code)}
                        >
                          <span className="text-sm text-content-primary">{q.nhan}</span>
                        </Checkbox>
                        {loiO && (
                          <p role="alert" className="mt-1 pl-6 text-xs text-danger-600">
                            {loiO}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            );
          })}
        </div>
      )}
    </div>
  );
}
